import * as React from "react";
import {
  KPIStrip,
  KPIStripItem,
  KPIStripItemLabel,
  KPIStripItemValue,
  KPIStripSkeleton,
} from "@/components/ui/kpi-strip";
import { Dot } from "@/components/ui/dot";
import { Notify } from "@/components/ui/notify";
import { useDashboardOverview } from "@/features/dashboard/hooks";
import { formatRelativeAge } from "@/lib/display-labels";
import { cn } from "@/lib/utils";
import type { LineSummary } from "@/features/lines/hooks";

const KPI_SUB = "mt-1 flex items-center gap-1.5 text-[12px] text-text-secondary";

export interface GlobalKPIStripProps {
  lines: LineSummary[] | undefined;
  loading?: boolean;
  // SensorsOffline/"produzido hoje" dependem de `lines`; sem isso, uma falha em
  // useLines() (independente de useDashboardOverview) deixava a tira travada num skeleton
  // eterno em vez de mostrar erro.
  linesIsError?: boolean;
  linesError?: unknown;
  onRetryLines?: () => void;
}

export function GlobalKPIStrip({
  lines,
  loading = false,
  linesIsError = false,
  linesError,
  onRetryLines,
}: GlobalKPIStripProps): React.ReactElement {
  const overview = useDashboardOverview();

  if (overview.isError || linesIsError) {
    return (
      <Notify.QueryError
        error={overview.isError ? overview.error : linesError}
        onRetry={() => {
          if (overview.isError) void overview.refetch();
          if (linesIsError) onRetryLines?.();
        }}
      />
    );
  }

  if (loading || !lines) return <KPIStripSkeleton items={5} />;

  const sensorsOffline = lines.reduce(
    (s, l) => s + (l.sensorHealth?.offline ?? 0) + (l.sensorHealth?.inconsistent ?? 0),
    0
  );

  const oeeAvg = overview.data?.oeeAverage24h ?? null;

  const alarmsCount = overview.data?.activeAlarmsCount ?? 0;
  const oldestAlarmAge = formatRelativeAge(overview.data?.oldestAlarmAt);
  const pendingJustifications = overview.data?.pendingJustificationsCount ?? 0;

  return (
    <KPIStrip aria-label="Indicadores globais da operação">
      <KPIStripItem>
        <KPIStripItemLabel>OEE médio (24h)</KPIStripItemLabel>
        <KPIStripItemValue>
          {overview.isLoading ? "…" : oeeAvg === null ? "—" : `${oeeAvg.toFixed(1)}%`}
        </KPIStripItemValue>
        {!overview.isLoading && oeeAvg === null ? (
          <p className={cn(KPI_SUB, "text-text-tertiary")}>sem produção nas últimas 24h</p>
        ) : null}
      </KPIStripItem>

      {}
      <KPIStripItem>
        <KPIStripItemLabel>Unidades produzidas hoje</KPIStripItemLabel>
        <KPIStripItemValue>
          {overview.isLoading
            ? "…"
            : overview.data
              ? overview.data.producedToday.toLocaleString("pt-BR")
              : "—"}
        </KPIStripItemValue>
        {overview.data && overview.data.producedToday === 0 && lines.length > 0 ? (
          <p className={cn(KPI_SUB, "text-text-tertiary")}>sem produção registrada hoje</p>
        ) : null}
      </KPIStripItem>

      {}
      <KPIStripItem accent={alarmsCount > 0}>
        <KPIStripItemLabel>Alarmes ativos</KPIStripItemLabel>
        <KPIStripItemValue>{alarmsCount}</KPIStripItemValue>
        {alarmsCount > 0 && oldestAlarmAge ? (
          <p className={KPI_SUB}>
            <Dot tone="danger" size={7} />
            mais antigo há {oldestAlarmAge}
          </p>
        ) : null}
      </KPIStripItem>

      {}
      <KPIStripItem accent={pendingJustifications > 0}>
        <KPIStripItemLabel>Justificativas pendentes</KPIStripItemLabel>
        <KPIStripItemValue>{pendingJustifications}</KPIStripItemValue>
        {pendingJustifications > 0 ? (
          <p className={KPI_SUB}>
            <Dot tone="warning" size={7} />
            aguardando causa
          </p>
        ) : null}
      </KPIStripItem>

      <KPIStripItem accent={sensorsOffline > 0}>
        <KPIStripItemLabel>Sensores offline</KPIStripItemLabel>
        <KPIStripItemValue>{sensorsOffline}</KPIStripItemValue>
        {sensorsOffline > 0 ? (
          <p className={KPI_SUB}>
            <Dot tone="warning" size={7} />
            requer atenção
          </p>
        ) : null}
      </KPIStripItem>
    </KPIStrip>
  );
}
